/**
 * MIRE layer config — layer-style JSON imports, schema version, and
 * `buildSystemLayers` (the MIRE counterpart to SafeCurves' layers builder).
 *
 * A single module (Curves) with a single layer (Curves), so envelope
 * assembly is much simpler than SafeCurves'.
 */

import type { LineLayerSpecification } from 'maplibre-gl';
import {
	ECurves,
	EMireModule,
	EProduct,
	type IMireFeature,
	type IMireSystemLayers
} from './interfaces';
import { getVectorLayerSource, groupByModule } from '#lib/common/layers.js';

import styleMireCurves from './layer_styles/mire_curves_style.json';

// GeoServer dataset this app owns; drives availability and layer filtering.
export const MIRE_DATASET = 'ableymire';

/**
 * Build the nested `IMireSystemLayers` from a flat list of operational features.
 * `appLayers` is expected to already be filtered to the active version (see
 * `loadSystem`); this keeps only the MIRE dataset and groups it by module.
 *
 * Returns null when MIRE product data is missing — caller treats this as a
 * system-data load failure.
 */
export function buildSystemLayers(appLayers: IMireFeature[]): IMireSystemLayers | null {
	const mireLayers = groupByModule(appLayers, EProduct.MIRE, MIRE_DATASET);
	if (!mireLayers) return null;

	return {
		[EMireModule.Curves]: {
			[ECurves.Curves]: getVectorLayerSource(
				ECurves.Curves,
				mireLayers[EMireModule.Curves]?.[0],
				{
					type: 'line',
					layout: styleMireCurves.layout as LineLayerSpecification['layout'],
					paint: styleMireCurves.paint as LineLayerSpecification['paint'],
					minzoom: 0
				},
				3
			)
		}
	};
}
